import React, { useState, useEffect } from 'react';
import InterestBuilder from './InterestBuilder';
import NegativeInterestBuilder from './NegativeInterestBuilder';
import './SmartQueryBuilder.css';

const SmartQueryBuilder = ({
    onQueryChange,
    initialQuery = '',
    disabled = false,
    showPreview = true
}) => {
    const [interests, setInterests] = useState([]);
    const [negativeInterests, setNegativeInterests] = useState([]);
    const [baseQuery, setBaseQuery] = useState('');

    // Extract initial interests from query if provided
    useEffect(() => {
        if (initialQuery) {
            // Simple extraction - could be enhanced with AI parsing
            const lowerQuery = initialQuery.toLowerCase();

            // Extract base query (remove interest-specific parts)
            let cleanQuery = initialQuery;
            cleanQuery = cleanQuery.replace(/who loves? [\w\s,]+/gi, '');
            cleanQuery = cleanQuery.replace(/but avoids? [\w\s,]+/gi, '');
            cleanQuery = cleanQuery.replace(/who likes? [\w\s,]+/gi, '');
            cleanQuery = cleanQuery.replace(/but dislikes? [\w\s,]+/gi, '');
            setBaseQuery(cleanQuery.trim());
        }
    }, [initialQuery]);

    const constructQuery = (baseQ, positiveInterests, negativeInterests) => {
        let query = baseQ || 'gifts';

        // Add positive interests
        if (positiveInterests.length > 0) {
            const interestList = positiveInterests.join(', ');
            if (positiveInterests.length === 1) {
                query += ` for someone who loves ${interestList}`;
            } else {
                query += ` for someone who loves ${interestList}`;
            }
        }

        // Add negative interests
        if (negativeInterests.length > 0) {
            const negativeList = negativeInterests.join(', ');
            if (negativeInterests.length === 1) {
                query += ` but avoids ${negativeList}`;
            } else {
                query += ` but avoids ${negativeList}`;
            }
        }

        return query;
    };

    // Update query whenever interests change
    useEffect(() => {
        const newQuery = constructQuery(baseQuery, interests, negativeInterests);
        onQueryChange(newQuery);
    }, [baseQuery, interests, negativeInterests, onQueryChange]);

    const handleAddInterest = (interest) => {
        if (!interests.includes(interest)) {
            setInterests(prev => [...prev, interest]);
        }
    };

    const handleRemoveInterest = (interest) => {
        setInterests(prev => prev.filter(i => i !== interest));
    };

    const handleAddNegativeInterest = (interest) => {
        if (!negativeInterests.includes(interest)) {
            setNegativeInterests(prev => [...prev, interest]);
        }
    };

    const handleRemoveNegativeInterest = (interest) => {
        setNegativeInterests(prev => prev.filter(i => i !== interest));
    };

    const handleBaseQueryChange = (e) => {
        setBaseQuery(e.target.value);
    };

    const clearAll = () => {
        setInterests([]);
        setNegativeInterests([]);
        setBaseQuery('');
    };

    const hasAnyFilters = interests.length > 0 || negativeInterests.length > 0 || baseQuery;

    // Generate example queries for placeholder
    const exampleQueries = [
        'birthday gifts',
        'anniversary presents',
        'christmas gifts',
        'graduation gifts',
        'baby shower gifts'
    ];

    const randomExample = exampleQueries[Math.floor(Math.random() * exampleQueries.length)];

    return (
        <div className="smart-query-builder">
            <div className="smart-query-header">
                <h3 className="smart-query-title">🔍 Smart Gift Search</h3>
                {hasAnyFilters && (
                    <button
                        type="button"
                        className="clear-all-button"
                        onClick={clearAll}
                        disabled={disabled}
                    >
                        Clear all
                    </button>
                )}
            </div>

            {/* Base Query Input */}
            <div className="base-query-section">
                <label className="base-query-label">
                    What kind of gifts are you looking for?
                </label>
                <input
                    type="text"
                    className="base-query-input"
                    placeholder={`e.g., "${randomExample}"`}
                    value={baseQuery}
                    onChange={handleBaseQueryChange}
                    disabled={disabled}
                />
            </div>

            {/* Interest Builders */}
            <div className="interest-builders">
                <InterestBuilder
                    interests={interests}
                    onAdd={handleAddInterest}
                    onRemove={handleRemoveInterest}
                    disabled={disabled}
                />

                <NegativeInterestBuilder
                    negativeInterests={negativeInterests}
                    onAdd={handleAddNegativeInterest}
                    onRemove={handleRemoveNegativeInterest}
                    disabled={disabled}
                />
            </div>

            {/* Query Preview */}
            {showPreview && (
                <div className="query-preview-section">
                    <label className="query-preview-label">
                        AI will search for:
                    </label>
                    <div className="query-preview">
                        {constructQuery(baseQuery, interests, negativeInterests)}
                    </div>
                </div>
            )}

            {/* Helper Text */}
            <div className="smart-query-helper">
                <p className="helper-text">
                    💡 <strong>Pro tip:</strong> The more specific you are about interests and things to avoid,
                    the better our AI can find perfect gifts!
                </p>
            </div>
        </div>
    );
};

export default SmartQueryBuilder; 